import React from 'react';
import { useRouting } from 'hooks/useRouting';
import { formatNumber } from 'utils/formatter';
import { DividendProps } from 'pages/DividendPrediction';
import { DividendColumnChart } from './DividendChart';
import {
  DividendPredictionModal,
  DividendListItemProps as DividendClassificationProps,
} from './DividendClassification';
import { DividendPredictionLoading } from './DividendError';
import { useScroll } from 'hooks/useScroll';

export const getSummaryDividendHaito = (
  data: Array<DividendProps | DividendClassificationProps>,
  isTaxDeduction: boolean
): number => {
  return data.reduce((acc, current): number => {
    const num = isTaxDeduction ? current.taxExcludedHaito : current.haito;
    return acc + num;
  }, 0);
};

const DividendList = ({
  data,
  isTaxDeduction,
}: {
  data: DividendListDataProps;
  isTaxDeduction: boolean;
}) => {
  useScroll();
  const routing = useRouting();
  const goDetail = (): void => {
    if (data.list.length === 0) return;
    routing.pushRouting(
      { path: `/dividendprediction/detail/${data.date}` },
      true,
      window.scrollY
    );
  };

  const isIncludeUnreleasedDividend = data.list.some(item => item.unreleased);
  const isAllUnreleasedDividend = data.list.every(item => item.unreleased);
  const unreleasedStatus =
    !isIncludeUnreleasedDividend && !isAllUnreleasedDividend
      ? 'not_exist'
      : isIncludeUnreleasedDividend && !isAllUnreleasedDividend
      ? 'not_all'
      : 'all';

  const sumDividend = getSummaryDividendHaito(data.list, isTaxDeduction);

  return (
    <div
      className={`grid items-center px-4 py-3 border-b border-gray-300 ${
        data.list.length ? 'cursor-pointer' : ''
      }`}
      style={{ gridTemplateColumns: '1fr 1fr' }}
      onClick={(): void => goDetail()}>
      <div className="ml-2 text-sm">{data.month}</div>
      <div className="flex flex-row items-center ml-auto">
        {sumDividend !== 0 ? (
          <>
            <div className="flex flex-col">
              <div
                className={`${
                  unreleasedStatus === 'all'
                    ? 'text-bluegray-400'
                    : 'text-bluegray-700'
                }`}>
                {formatNumber(sumDividend, {})}
                <span className="ml-1 text-xs">円</span>
              </div>
              {unreleasedStatus !== 'not_exist' ? (
                <div className="ml-auto text-1.5xs text-bluegray-400">
                  (未発表{unreleasedStatus === 'not_all' ? '含む' : ''})
                </div>
              ) : (
                <></>
              )}
            </div>
          </>
        ) : (
          '---'
        )}
        <div className="flex justify-end w-8">
          {data.list.length !== 0 ? (
            <div className="icon-chevron-right flex text-2xl text-gray-500"></div>
          ) : (
            <></>
          )}
        </div>
      </div>
    </div>
  );
};

type DividendListDataProps = {
  date: string;
  month: string;
  list: DividendProps[];
};

// 推移
export const DividendTransition = ({
  dividendData,
  selectedDate,
  isTaxDeduction,
}: {
  dividendData: DividendProps[];
  selectedDate: string;
  isTaxDeduction: boolean;
}) => {
  const dividendListData = [...Array(12)].map((_, i) => {
    const month = (i + 1).toString();
    const date = `${selectedDate}${month.padStart(2, '0')}`;
    const list = dividendData.filter(data => data.haitoYm === date);

    return {
      date: date,
      month: `${month}月`,
      list: list,
    };
  }) as DividendListDataProps[];

  if (dividendListData === null)
    return <DividendPredictionLoading str={'月'} />;

  return (
    <>
      <div className="mt-4 ml-1">
        <DividendColumnChart
          {...{ dividendData, selectedDate, isTaxDeduction }}
        />
      </div>
      <div className="mx-4 text-sm text-bluegray-550">
        ※ 未発表は昨年の実績を元に算出
      </div>
      <DividendPredictionModal />
      <div
        className="grid mt-4 px-4 py-1 text-sm text-bluegray-550 font-bold bg-gray-300"
        style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div className="ml-2">月</div>
        <div>予想額</div>
      </div>
      <div className="text-bluegray-700">
        {dividendListData.map((data, i) => (
          <DividendList {...{ data, isTaxDeduction }} key={i} />
        ))}
      </div>
    </>
  );
};
